import { getPlatform, getPlayerPlatformHeader } from '@/platform'

export const PLATFORM = getPlatform()
/** Value for x-player-platform header (android | web). Resolved at call time so initPlatform() can run first. */
export const getPlatformHeader = (): string => getPlayerPlatformHeader()

// TODO: these values should be provided by an api endpoint
export const DURABLE_OBJECT_ALARM_PERIOD_MS = 10 * 1000
export const PING_PERIOD_MS = 60 * 1000 // 30 seconds

/** Aligns audience motion aggregates with `VBuffer` playback-heartbeat cadence. */
export const PLAYBACK_HEARTBEAT_INTERVAL_MS = 60 * 1000
/**
 * Lead before the wall-clock 15-minute boundary so the PoP flush reaches the API before
 * server `at` crosses into the next bucket (RTT / queue delay).
 */
export const PROOF_OF_PLAY_WALL_BUCKET_FLUSH_LEAD_MS = 500
/** Max rate for extra playback-heartbeats when viewport or rotation changes (dashboard screen aspect column). */
export const SCREEN_LAYOUT_HEARTBEAT_DEBOUNCE_MS = 1_000
/**
 * Extra playback-heartbeat after mains power is plugged / unplugged (#2528). Trailing edge, so bursts of
 * connect/disconnect events send one heartbeat, and the battery reading has time to settle.
 */
export const POWER_SOURCE_HEARTBEAT_DEBOUNCE_MS = 2_000
export const SOCKET_TIMEOUT_MS = 5 * 1000 // 5 seconds

/** Brief pause after the first queue is ready so the “Baixando mídias” screen can register before playback. */
export const POST_PLAYLIST_LOAD_HOLD_MS = 140

/**
 * Legacy cap for callers that still race registration bootstrap; boot splash now dismisses after paint.
 */
export const REGISTRATION_BOOTSTRAP_SPLASH_MAX_MS = 20_000

/**
 * Last-resort time `#app-loading` may cover a registered player waiting for the first
 * painted frame. Hybrid video dismisses on Media3 `onFirstFrame`, not `onReady`.
 * A 4s cap revealed loading chrome then a black hybrid gap.
 */
export const BOOT_PLAYBACK_COVER_MAX_MS = 30_000

/**
 * Short cover when there is nothing to paint yet (empty queue / weekly availability off).
 * Avoids holding the boot splash for {@link BOOT_PLAYBACK_COVER_MAX_MS} with no content.
 */
export const BOOT_PLAYBACK_COVER_IDLE_MAX_MS = 4_000

/**
 * If campaign media loading finishes sooner than this, do not show the download idle screen or the post-load hold
 * (avoids flashing “Baixando mídias” for fast cache/network). The idle download UI only appears after this delay
 * while loading is still in progress.
 */
export const MEDIA_LOAD_CHROME_DELAY_MS = 1_000

/**
 * After the QR code disappears, keep “waiting” briefly before “Baixando mídias” to reduce phase thrash.
 * Kept short so registration still feels immediate.
 */
export const REGISTRATION_IDLE_SETTLE_MS = 180

/**
 * Unregistered (pairing) player: interval for `scheduleRegistrationRefetch` →
 * `getPlayerRegistrationAndRegistrationStatus` (GET /registration + downstream). Aligns polling
 * with how often we refresh pairing + web OTA hints from the API vs WS-only state.
 */
export const PAIRING_REGISTRATION_REFETCH_INTERVAL_MS = 4 * 1000

/**
 * Registered player with WebSocket connected: interval for `scheduleRegistrationRefetch` so a missed
 * `player-unregistered` push is corrected via registration-status. Aligns with
 * `PLAYER_POOLING_PERIOD_MS` (poll fallback when WS is down).
 */
export const REGISTRATION_REFETCH_INTERVAL_MS = 30 * 1000

/** Static `<video poster>` (black). Do not toggle with slot visibility — Android WebView can keep a stale poster layer. */
const VIDEO_POSTER_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080"><rect width="100%" height="100%" fill="#000"/></svg>'
export const VIDEO_POSTER_SRC = `data:image/svg+xml,${encodeURIComponent(VIDEO_POSTER_SVG)}`

/** Interval (ms) for re-evaluating playlist time blocks. Override with VITE_QUEUE_REBUILD_INTERVAL_MS. */
export const QUEUE_REBUILD_INTERVAL_MS = (() => {
  const raw = import.meta.env.VITE_QUEUE_REBUILD_INTERVAL_MS
  if (raw == null || raw === '') { return 1_000 }
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 ? n : 1_000
})()

export const VERSION = __APP_VERSION__
export const MODE = import.meta.env.MODE
